import { memo } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Clock, Eye, Check, Undo2, Hand, Phone, MessageCircle, ThumbsDown } from 'lucide-react';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { TempoTriagemBadge } from '@/components/kanban/TempoTriagemBadge';
import {
  CancelarItemTriagemButton,
  ContatoPedidoCell,
  TimelineCnpjButton,
  formatCnpjTriagem,
  resumoProdutos,
} from '@/components/kanban/triagem/triagemPedidoUtils';
import type { PedidoTriagemBiometria } from './useTriagemBiometria';
import { TRIAGEM_SELO_BASE, TRIAGEM_SELO_PENDENTE, TRIAGEM_BTN, TRIAGEM_BTN_ATIVO, TRIAGEM_BTN_PRIMARIO } from '@/components/kanban/triagem/triagemTabela';
import { AcoesTriagem, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';

interface TriagemBiometriaRowProps {
  pedido: PedidoTriagemBiometria;
  posicao: number;
  isProcessing: boolean;
  /** BKO: manter, devolver p/ Criação, mandar p/ Aceite e cancelar. Para o comercial ficam apagados. */
  podeAcoesBko: boolean;
  /** Comercial: marca "já fiz" para o BKO conferir. Não move o pedido. */
  podeSinalizar: boolean;
  isSinalizando: boolean;
  onVoltarCriacao: (pedido: PedidoTriagemBiometria) => void;
  onEnviarAceite: (pedido: PedidoTriagemBiometria) => void;
  onAlternarSinalizacao: (pedido: PedidoTriagemBiometria) => void;
  /** BKO conferiu e a biometria não saiu: derruba o 🖐 e avisa o consultor. */
  onMarcarNaoFeito: (pedido: PedidoTriagemBiometria) => void;
  /** Manter: justificar e passar para o fim da fila (`manter_em`). */
  onManterFimFila: (pedido: PedidoTriagemBiometria) => void;
  onAfterCancel: () => void;
}

function rotuloTempoSinal(marcadoEm: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(marcadoEm).getTime()) / 60000));
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
}

export const TriagemBiometriaRow = memo(function TriagemBiometriaRow({
  pedido,
  posicao,
  isProcessing,
  podeAcoesBko,
  podeSinalizar,
  isSinalizando,
  onVoltarCriacao,
  onEnviarAceite,
  onAlternarSinalizacao,
  onMarcarNaoFeito,
  onManterFimFila,
  onAfterCancel,
}: TriagemBiometriaRowProps) {
  const { open: openPedido } = usePainelPedido();
  const { resumo, completo } = resumoProdutos(pedido.itens);
  const qtdTotal = pedido.itens.reduce((sum, i) => sum + (i.quantidade || 0), 0);
  const naoFeito = pedido.sinal_estado === 'nao_feito';
  const sinalizado = !!pedido.sinalizado_em && !naoFeito;
  const quando = pedido.sinalizado_em ? format(new Date(pedido.sinalizado_em), 'dd/MM HH:mm') : '';
  const tituloSinal = naoFeito
    ? `Conferido e NÃO feito${pedido.sinalizado_por ? ` por ${pedido.sinalizado_por}` : ''} em ${quando}${pedido.sinal_observacao ? ` — "${pedido.sinal_observacao}"` : ''}`
    : sinalizado
      ? `Sinalizado como feito${pedido.sinalizado_por ? ` por ${pedido.sinalizado_por}` : ''} em ${quando}`
      : 'Sem sinalização do comercial';

  /* §7 da régua: destaque é filete POR DENTRO + fundo quente, e só para quem
     precisa de decisão AGORA. Aqui são os dois estados do sinal — o 🖐 é o que a
     BKO tem que conferir, o 👎 é o que está esperando o consultor. A cor separa
     um do outro; o resto da fila fica neutro. */
  const rowClass = sinalizado
    ? 'box-border h-[33px] border-b border-border/30 bg-accent/40 shadow-[inset_3px_0_0_hsl(var(--primary))] hover:bg-accent/70 transition-colors'
    : naoFeito
      ? 'box-border h-[33px] border-b border-border/30 bg-accent/20 shadow-[inset_3px_0_0_hsl(var(--primary))] hover:bg-accent/50 transition-colors'
      : 'box-border h-[33px] border-b border-border/30 hover:bg-muted/30 transition-colors';

  return (
    <tr className={rowClass}>
      <td className="px-2 py-0 align-middle">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
          {posicao + 1}º
          {pedido.manter_em && <Clock className="h-3 w-3 text-primary" aria-label="Passado para o fim da fila" />}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <CopyOnClick value={pedido.documento || pedido.cnpj} className="tabular-nums text-xs">
          {formatCnpjTriagem(pedido.documento || pedido.cnpj)}
        </CopyOnClick>
      </td>

      {/* Razão social + GESTOR: é para ele que se liga na biometria, então o
          nome tem que estar na frente de quem vai discar. */}
      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-foreground truncate block max-w-[140px]" title={pedido.razao_social}>
          {pedido.razao_social || '-'}
        </span>
        {pedido.gestor_nome && (
          <span
            className="text-[10px] text-muted-foreground truncate block max-w-[140px]"
            title={`Gestor (quem faz a biometria): ${pedido.gestor_nome}${pedido.telefone ? ` · ${pedido.telefone}` : ''}`}
          >
            {pedido.gestor_nome}
          </span>
        )}
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground truncate block max-w-[110px]" title={`${pedido.consultor_nome}${pedido.equipe_nome ? ` • ${pedido.equipe_nome}` : ''}`}>
          {pedido.consultor_nome || '-'}{pedido.equipe_nome ? ` • ${pedido.equipe_nome}` : ''}
        </span>
      </td>

      {/* Dono do pedido: quem pegou lá no Pedido Fechado segue responsável aqui */}
      <td className="px-2 py-0 align-middle overflow-hidden">
        {pedido.dono_id ? (
          <span className="text-xs text-muted-foreground truncate block" title={`Pegou e criou: ${pedido.dono_nome}`}>
            {pedido.dono_nome || '-'}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground/50" title="Sem Dono registrado (pedido anterior à regra de pegar)">-</span>
        )}
      </td>

      <td className="px-2 py-0 align-middle">
        <button
          onClick={(e) => { e.stopPropagation(); openPedido(pedido.pedido_id, 'visao360'); }}
          className="text-xs font-medium text-primary hover:underline cursor-pointer"
          title="Abrir Painel do Pedido"
        >
          {pedido.numero_pedido}
        </button>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground truncate block" title={completo}>
          {resumo || '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground">{qtdTotal || '-'}</span>
      </td>

      {/* Faturamento e aparelho lado a lado, NUNCA somados: o aparelho tem KPI
          próprio e não é faturamento de linha. Pedido só de aparelho mostra só o
          valor do aparelho — em vez de "-", que faria parecer pedido sem valor. */}
      <td className="px-2 py-0 align-middle">
        <span className="flex items-baseline gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
          {pedido.valor_total > 0
            ? `R$ ${pedido.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
            : pedido.valor_aparelhos > 0
              ? null
              : '-'}
          {pedido.valor_aparelhos > 0 && (
            <span
              className="text-[10.5px] font-semibold text-muted-foreground/80"
              title="Aparelho — contado à parte, fora do faturamento"
            >
              {pedido.valor_total > 0 ? '+ ' : ''}
              {`R$ ${pedido.valor_aparelhos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} apar.
            </span>
          )}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <TempoTriagemBadge entradaColunaEm={pedido.entrada_coluna_em} prazoHoras={5} />
      </td>

      {/* Sinal do comercial: "já cobrei / o cliente já fez" — o BKO confere */}
      <td className="px-2 py-0 align-middle">
        {naoFeito ? (
          /* MESMO formato do 🖐: ícone + tempo numa linha só. "não feito" por
             extenso quebrava em duas linhas e esticava a altura da linha — o
             que diferencia é o ícone e a cor, e o resto está no title. */
          <span
            className={`${TRIAGEM_SELO_BASE} ${TRIAGEM_SELO_PENDENTE} whitespace-nowrap`}
            title={tituloSinal}
          >
            <ThumbsDown className="h-2.5 w-2.5 flex-shrink-0" />
            <span>{rotuloTempoSinal(pedido.sinalizado_em as string)}</span>
          </span>
        ) : sinalizado ? (
          <span
            className="inline-flex items-center gap-0.5 whitespace-nowrap rounded border border-border bg-muted px-1 py-0.5 text-[10px] font-medium text-primary dark:border-primary dark:bg-muted/40 dark:text-primary"
            title={tituloSinal}
          >
            <Hand className="h-2.5 w-2.5 flex-shrink-0" />
            <span>{rotuloTempoSinal(pedido.sinalizado_em as string)}</span>
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground/40" title={tituloSinal}>—</span>
        )}
      </td>

      {/* Cobrança: ligar + conversa no WhatsApp Inbox (todo pedido nasce de uma conversa) */}
      <td className="px-2 py-0 align-middle">
        {/* Tudo em UMA linha (Douglas 08/08): ícones + contador de cobranças
            inline. O contador zera sozinho se o pedido sair e voltar (recorte
            por entrada_coluna_em). Nada = ninguém encostou nele ainda. */}
        <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap">
          <ContatoPedidoCell
            telefone={pedido.telefone}
            leadOrigemId={pedido.lead_origem_id}
            cnpj={pedido.cnpj}
            telefonesAlternativos={pedido.telefones}
            nomeContato={pedido.gestor_nome}
          />
          <TimelineCnpjButton cnpj={pedido.cnpj} />
          {(pedido.ligacoes > 0 || pedido.whats > 0) && (
            <span
              className="flex items-center gap-1 ml-0.5 text-[10px] tabular-nums text-muted-foreground shrink-0"
              title={`Desde que entrou na biometria: ${pedido.ligacoes} ligação(ões) e ${pedido.whats} mensagem(ns) enviada(s) para ${pedido.gestor_nome || 'o gestor'}`}
            >
              {pedido.ligacoes > 0 && (
                <span className="flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" />{pedido.ligacoes}</span>
              )}
              {pedido.whats > 0 && (
                <span className="flex items-center gap-0.5"><MessageCircle className="h-2.5 w-2.5" />{pedido.whats}</span>
              )}
            </span>
          )}
        </div>
      </td>

      <td className="px-2 py-0 align-middle">
        {/* Os mesmos botões para todo mundo (Douglas 14/10/2026): o que não é do
            papel de quem olha fica apagado e diz o porquê, nunca some. */}
        <AcoesTriagem
          manter={
            <BotaoManter
              title={podeAcoesBko
                ? 'Manter — consultei e a biometria ainda não saiu. Justifique e passe para o fim da fila'
                : 'Ação do BKO'}
              onClick={() => onManterFimFila(pedido)}
              disabled={!podeAcoesBko || isProcessing}
            />
          }
          avancar={
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN_PRIMARIO}
              title={podeAcoesBko ? 'Biometria concluída — enviar para ACEITE (pedido inteiro)' : 'Ação do BKO'}
              onClick={() => onEnviarAceite(pedido)}
              disabled={!podeAcoesBko || isProcessing}
            >
              <Check className="h-3.5 w-3.5" />
            </Button>
          }
          desvios={
            <>
              <Button
                size="icon"
                variant="ghost"
                className={
                  sinalizado
                    ? TRIAGEM_BTN_ATIVO
                    : TRIAGEM_BTN
                }
                title={
                  !podeSinalizar
                    ? 'Só para o consultor'
                    : sinalizado
                      ? `${tituloSinal} — clique para desfazer`
                      : 'Avisar o BKO que já fiz (cobrei / o cliente fez a biometria). Não tira o pedido da fila.'
                }
                onClick={() => onAlternarSinalizacao(pedido)}
                disabled={!podeSinalizar || isSinalizando}
              >
                <Hand className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className={naoFeito ? TRIAGEM_BTN_ATIVO : TRIAGEM_BTN}
                title={!podeAcoesBko
                  ? 'Ação do BKO'
                  : naoFeito
                    ? `${tituloSinal} — clique para limpar a marca`
                    : 'Conferi e a biometria NÃO foi feita — derruba o "já fiz" e avisa o consultor'}
                onClick={() => onMarcarNaoFeito(pedido)}
                disabled={!podeAcoesBko || isSinalizando}
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className={TRIAGEM_BTN}
                title={podeAcoesBko ? 'Devolver para CRIAÇÃO PEDIDO (pedido inteiro)' : 'Ação do BKO'}
                onClick={() => onVoltarCriacao(pedido)}
                disabled={!podeAcoesBko || isProcessing}
              >
                <Undo2 className="h-3.5 w-3.5" />
              </Button>
            </>
          }
          ferramentas={
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN}
              title="Analisar — Abrir Painel do Pedido (Pedido 360)"
              onClick={() => openPedido(pedido.pedido_id, 'visao360')}
            >
              <Eye className="h-3 w-3" />
            </Button>
          }
          cancelar={
            <CancelarItemTriagemButton
              pedidoId={pedido.pedido_id}
              itens={pedido.itens}
              disabled={!podeAcoesBko || isProcessing}
              disabledTitle={!podeAcoesBko ? 'Ação do BKO' : 'Aguarde a ação em andamento'}
              onAfterCancel={onAfterCancel}
            />
          }
        />
      </td>
    </tr>
  );
});
